import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Avatar,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  Public as EveryoneIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

import {
  workspaceApi,
  WorkspaceAclEntry,
  WorkspaceAclState,
  WorkspaceGroup,
  WorkspacePermission,
  WorkspaceResourceType,
} from '../../utils/workspaceApi';
import { userDirectoryApi, ShareTargetDto } from '../../utils/userDirectoryApi';

const PERMISSION_LABELS: Record<WorkspacePermission, string> = {
  VIEW: 'Can view',
  EDIT: 'Can edit',
  MANAGE: 'Full access',
};

const SOURCE_COPY: Record<WorkspaceAclState['sourceType'], string> = {
  ITEM: 'Access rules are set directly on this item.',
  FOLDER: 'Access is inherited from folder',
  PERSONAL_DEFAULT: 'Private: only the workspace owner can see this.',
  COMPANY_DEFAULT: 'Everyone in your organisation can see this by default.',
};

type PendingPrincipal =
  | { kind: 'USER'; username: string; displayName: string }
  | { kind: 'GROUP'; group: WorkspaceGroup }
  | { kind: 'EVERYONE' };

type AccessManagementSectionProps = {
  resourceType: WorkspaceResourceType;
  resourceKey: string;
  /** Bumps to trigger a reload after external changes (e.g. moving the item). */
  refreshKey?: number;
  callerUsername?: string | null;
  onSaved?: () => void | Promise<void>;
};

/**
 * "Who has access" section: shows the effective rules (including where they are inherited
 * from), and lets users with full access add/remove user, group and everyone rules.
 */
const AccessManagementSection: React.FC<AccessManagementSectionProps> = ({
  resourceType,
  resourceKey,
  refreshKey = 0,
  callerUsername = null,
  onSaved,
}) => {
  const [acl, setAcl] = useState<WorkspaceAclState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [principalQuery, setPrincipalQuery] = useState('');
  const [userOptions, setUserOptions] = useState<ShareTargetDto[]>([]);
  const [selectedPrincipal, setSelectedPrincipal] = useState<PendingPrincipal | null>(null);
  const [selectedPermission, setSelectedPermission] = useState<WorkspacePermission>('VIEW');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [aclState, groupList] = await Promise.all([
          workspaceApi.getAcl(resourceType, resourceKey),
          workspaceApi.listGroups().catch(() => [] as WorkspaceGroup[]),
        ]);
        if (!cancelled) {
          setAcl(aclState);
          setGroups(groupList);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.data?.message || e?.message || 'Failed to load access rules.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [resourceType, resourceKey, refreshKey]);

  useEffect(() => {
    const query = principalQuery.trim();
    if (query.length < 2) {
      setUserOptions([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const users = await userDirectoryApi.searchShareTargets(query);
        if (!cancelled) {
          setUserOptions(users);
        }
      } catch {
        if (!cancelled) {
          setUserOptions([]);
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [principalQuery]);

  const principalOptions: PendingPrincipal[] = useMemo(() => {
    const options: PendingPrincipal[] = [{ kind: 'EVERYONE' }];
    for (const group of groups) {
      options.push({ kind: 'GROUP', group });
    }
    for (const user of userOptions) {
      options.push({ kind: 'USER', username: user.username, displayName: user.displayName });
    }
    // freeSolo-style manual username entry stays possible via the query text itself.
    return options;
  }, [groups, userOptions]);

  const optionLabel = (option: PendingPrincipal): string => {
    switch (option.kind) {
      case 'EVERYONE':
        return 'Everyone in the organisation';
      case 'GROUP':
        return `Group: ${option.group.name}`;
      case 'USER':
        return option.displayName !== option.username
          ? `${option.displayName} (${option.username})`
          : option.username;
      default:
        return '';
    }
  };

  const persist = useCallback(
    async (entries: WorkspaceAclEntry[]) => {
      setSaving(true);
      setError(null);
      try {
        const next = await workspaceApi.setAcl(
          resourceType,
          resourceKey,
          entries.map(({ principalType, principalId, permission }) => ({
            principalType,
            principalId,
            permission,
          }))
        );
        setAcl(next);
        await onSaved?.();
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Failed to update access rules.');
      } finally {
        setSaving(false);
      }
    },
    [resourceType, resourceKey, onSaved]
  );

  const addRule = async () => {
    if (!acl) {
      return;
    }
    let principal: PendingPrincipal | null = selectedPrincipal;
    // Allow adding a raw username typed into the box (directory may be degraded).
    if (!principal && principalQuery.trim().length > 0) {
      principal = {
        kind: 'USER',
        username: principalQuery.trim(),
        displayName: principalQuery.trim(),
      };
    }
    if (!principal) {
      return;
    }
    const newEntry: WorkspaceAclEntry =
      principal.kind === 'EVERYONE'
        ? { principalType: 'EVERYONE', principalId: '*', permission: selectedPermission }
        : principal.kind === 'GROUP'
          ? {
              principalType: 'GROUP',
              principalId: principal.group.id,
              permission: selectedPermission,
            }
          : {
              principalType: 'USER',
              principalId: principal.username,
              permission: selectedPermission,
            };

    const existing = acl.entries.filter(
      (entry) =>
        !(
          entry.principalType === newEntry.principalType &&
          entry.principalId === newEntry.principalId
        )
    );
    // The first explicit ACL replaces company role defaults. Keep the current manager able to
    // repair the rules, but do not seed EVERYONE:VIEW (which would silently downgrade engineers
    // and analysts while still changing the default audience).
    const callerAlreadyGetsDirectManage =
      newEntry.principalType === 'USER' &&
      newEntry.principalId === callerUsername &&
      newEntry.permission === 'MANAGE';
    const seed: WorkspaceAclEntry[] =
      acl.entries.length === 0 &&
      (acl.sourceType === 'COMPANY_DEFAULT' || acl.sourceType === 'FOLDER') &&
      callerUsername &&
      !callerAlreadyGetsDirectManage
        ? [{ principalType: 'USER', principalId: callerUsername, permission: 'MANAGE' }]
        : [];
    await persist([...seed, ...existing, newEntry]);
    setSelectedPrincipal(null);
    setPrincipalQuery('');
    setSelectedPermission('VIEW');
  };

  const removeRule = async (target: WorkspaceAclEntry) => {
    if (!acl) {
      return;
    }
    await persist(
      acl.entries.filter(
        (entry) =>
          !(
            entry.principalType === target.principalType &&
            entry.principalId === target.principalId
          )
      )
    );
  };

  const principalIcon = (entry: WorkspaceAclEntry) => {
    if (entry.principalType === 'GROUP') {
      return <GroupsIcon fontSize="small" />;
    }
    if (entry.principalType === 'EVERYONE') {
      return <EveryoneIcon fontSize="small" />;
    }
    return <PersonIcon fontSize="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {error && <Alert severity="error">{error}</Alert>}
      {acl && (
        <>
          <Typography variant="body2" color="text.secondary">
            {acl.sourceType === 'FOLDER' ? (
              <>
                {SOURCE_COPY.FOLDER}{' '}
                <strong>{acl.sourceFolderName || 'a parent folder'}</strong>.
              </>
            ) : (
              SOURCE_COPY[acl.sourceType]
            )}
          </Typography>

          {acl.canManage &&
            (acl.sourceType === 'COMPANY_DEFAULT' || acl.sourceType === 'FOLDER') &&
            acl.entries.length === 0 && (
            <Alert severity="warning">
              The first direct rule replaces inherited/default access. Existing users may lose
              access unless you add them explicitly; your full access will be retained.
            </Alert>
          )}

          {acl.entries.length > 0 && (
            <List dense disablePadding>
              {acl.entries.map((entry) => (
                <ListItem
                  key={`${entry.principalType}:${entry.principalId}`}
                  disableGutters
                  secondaryAction={
                    acl.canManage ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          select
                          size="small"
                          value={entry.permission}
                          disabled={saving}
                          onChange={(event) => {
                            const permission = event.target.value as WorkspacePermission;
                            void persist(
                              acl.entries.map((candidate) =>
                                candidate.principalType === entry.principalType &&
                                candidate.principalId === entry.principalId
                                  ? { ...candidate, permission }
                                  : candidate
                              )
                            );
                          }}
                          sx={{ minWidth: 130 }}
                        >
                          {(Object.keys(PERMISSION_LABELS) as WorkspacePermission[]).map(
                            (permission) => (
                              <MenuItem
                                key={permission}
                                value={permission}
                                disabled={
                                  permission === 'MANAGE' && entry.principalType === 'EVERYONE'
                                }
                              >
                                {PERMISSION_LABELS[permission]}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                        <Tooltip title="Remove access">
                          <IconButton
                            edge="end"
                            size="small"
                            disabled={saving}
                            aria-label={`Remove access for ${entry.principalName || entry.principalId}`}
                            onClick={() => void removeRule(entry)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {PERMISSION_LABELS[entry.permission]}
                      </Typography>
                    )
                  }
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 28, height: 28 }}>{principalIcon(entry)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      entry.principalType === 'EVERYONE'
                        ? 'Everyone in the organisation'
                        : entry.principalName || entry.principalId
                    }
                    secondary={
                      entry.principalType === 'GROUP'
                        ? 'Group'
                        : entry.principalType === 'EVERYONE'
                          ? undefined
                          : 'User'
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {acl.canManage && (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                size="small"
                fullWidth
                options={principalOptions}
                value={selectedPrincipal}
                getOptionLabel={optionLabel}
                isOptionEqualToValue={(a, b) => optionLabel(a) === optionLabel(b)}
                onChange={(_event, value) => {
                  setSelectedPrincipal(value);
                  if (value?.kind === 'EVERYONE' && selectedPermission === 'MANAGE') {
                    setSelectedPermission('VIEW');
                  }
                }}
                inputValue={principalQuery}
                onInputChange={(_event, value) => setPrincipalQuery(value)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Add a person, group, or everyone…" />
                )}
              />
              <TextField
                select
                size="small"
                value={selectedPermission}
                onChange={(event) =>
                  setSelectedPermission(event.target.value as WorkspacePermission)
                }
                sx={{ minWidth: 130 }}
              >
                {(Object.keys(PERMISSION_LABELS) as WorkspacePermission[]).map((permission) => (
                  <MenuItem
                    key={permission}
                    value={permission}
                    disabled={permission === 'MANAGE' && selectedPrincipal?.kind === 'EVERYONE'}
                  >
                    {PERMISSION_LABELS[permission]}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                disabled={saving || (!selectedPrincipal && principalQuery.trim().length === 0)}
                onClick={() => void addRule()}
              >
                Add
              </Button>
            </Stack>
          )}

          <Typography variant="caption" color="text.secondary">
            Need a new team grouping?{' '}
            <MuiLink component={RouterLink} to="/groups">
              Manage user groups
            </MuiLink>
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default AccessManagementSection;
